import { useTranslation } from 'react-i18next'
import {
  Package, Globe, Users, TrendingUp, ThumbsUp, ThumbsDown,
  Lightbulb, MapPin, DollarSign, Rocket, Target,
} from 'lucide-react'
import type { ProductSummary } from '@/lib/api/amazon-listing'

const CARD = 'bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5'
const LABEL = 'text-xs font-medium text-neutral-500 dark:text-neutral-400'
const VALUE = 'text-sm text-neutral-900 dark:text-white'
const TAG_GREEN = 'px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
const TAG_BLUE = 'px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
const TAG_PURPLE = 'px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
const TAG_NEUTRAL = 'px-2 py-0.5 rounded-full text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'

/** Safely coerce a value to an array (API sometimes returns objects instead of arrays). */
function asArray<T>(val: T[] | undefined | null): T[] {
  return Array.isArray(val) ? val : []
}

function SectionHeader({ icon: Icon, label, color }: { icon: React.ElementType; label: string; color: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`h-4 w-4 text-${color}-500`} />
      <h3 className="font-semibold text-sm text-neutral-900 dark:text-white">{label}</h3>
    </div>
  )
}

export function ProductIdentityCard({ data }: { data: ProductSummary['product_identity'] }) {
  const { t } = useTranslation()
  const fields = [
    { label: t('amazonListing.productSummary.whatItIs'), value: data.what_it_is },
    { label: t('amazonListing.productSummary.quantity'), value: data.quantity_contents },
    { label: t('amazonListing.productSummary.category'), value: data.category_niche },
    { label: t('amazonListing.productSummary.complexity'), value: data.complexity },
  ]
  return (
    <div className={CARD}>
      <SectionHeader icon={Package} label={t('amazonListing.productSummary.productIdentity')} color="blue" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {fields.map(f => (
          <div key={f.label}>
            <div className={LABEL}>{f.label}</div>
            <div className={VALUE}>{f.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function MarketLandscapeCard({ data }: { data: ProductSummary['market_landscape'] }) {
  const { t } = useTranslation()
  const players = asArray(data.major_players)
  const trends = asArray(data.market_trends)
  return (
    <div className={CARD}>
      <SectionHeader icon={Globe} label={t('amazonListing.productSummary.marketLandscape')} color="blue" />
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm">
          <span className={LABEL}>{t('amazonListing.productSummary.marketSize')}</span>
          <span className={TAG_BLUE}>{data.market_size_indicator}</span>
          <span className={LABEL}>{t('amazonListing.productSummary.competitorCount')}</span>
          <span className={VALUE}>{data.competitor_count_estimate}</span>
        </div>
        {players.length > 0 && (
          <div className="space-y-1">
            <div className={LABEL}>{t('amazonListing.productSummary.majorPlayers')}</div>
            <div className="flex flex-wrap gap-1.5">
              {players.map((p, i) => (
                <span key={i} className={TAG_BLUE} title={p.positioning}>{p.brand}</span>
              ))}
            </div>
          </div>
        )}
        {trends.length > 0 && (
          <div className="space-y-1">
            <div className={LABEL}>{t('amazonListing.productSummary.marketTrends')}</div>
            <div className="flex flex-wrap gap-1.5">
              {trends.map((tr, i) => (
                <span key={i} className={TAG_NEUTRAL}>{tr}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function CustomerVoiceCard({ data }: { data: ProductSummary['customer_voice'] }) {
  const { t } = useTranslation()
  const positives = asArray(data.top_positives)
  const negatives = asArray(data.top_negatives)
  return (
    <div className={CARD}>
      <SectionHeader icon={Users} label={t('amazonListing.productSummary.customerVoice')} color="green" />
      <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3 font-medium">{data.sentiment_summary}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {positives.length > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <ThumbsUp className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-700 dark:text-green-400">{t('amazonListing.productSummary.positives')}</span>
            </div>
            <ul className="space-y-1.5">
              {positives.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm text-green-800 dark:text-green-300">
                  <span className="text-green-500 mt-0.5 shrink-0">+</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {negatives.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <ThumbsDown className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
              <span className="text-xs font-semibold text-red-700 dark:text-red-400">{t('amazonListing.productSummary.negatives')}</span>
            </div>
            <ul className="space-y-1.5">
              {negatives.map((n, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm text-red-800 dark:text-red-300">
                  <span className="text-red-500 mt-0.5 shrink-0">−</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export function UseCasesCard({ data }: { data: ProductSummary['use_cases'] }) {
  const { t } = useTranslation()
  const typical = asArray(data.typical)
  const atypical = asArray(data.atypical)
  return (
    <div className={CARD}>
      <SectionHeader icon={Lightbulb} label={t('amazonListing.productSummary.useCases')} color="orange" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className={LABEL + ' mb-1.5'}>{t('amazonListing.productSummary.typical')}</div>
          <div className="space-y-1">{typical.map((u, i) => <div key={i} className={TAG_BLUE}>{u}</div>)}</div>
        </div>
        <div>
          <div className={LABEL + ' mb-1.5'}>{t('amazonListing.productSummary.atypical')}</div>
          <div className="space-y-1">{atypical.map((u, i) => <div key={i} className={TAG_PURPLE}>{u}</div>)}</div>
        </div>
      </div>
    </div>
  )
}

export function RegionalInfoCard({ data }: { data: ProductSummary['regional_info'] }) {
  const { t } = useTranslation()
  const names = asArray(data.regional_names)
  return (
    <div className={CARD}>
      <SectionHeader icon={MapPin} label={t('amazonListing.productSummary.regionalInfo')} color="neutral" />
      <div className="space-y-2 text-sm">
        <div><span className={LABEL}>{t('amazonListing.productSummary.origin')}: </span><span className={VALUE}>{data.origin}</span></div>
        {names.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {names.map((n, i) => <span key={i} className={TAG_BLUE}>{n}</span>)}
          </div>
        )}
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{data.regional_preferences}</p>
      </div>
    </div>
  )
}

export function KeyFactsCard({ data }: { data: ProductSummary['key_facts'] }) {
  const { t } = useTranslation()
  const points = asArray(data.data_points)
  return (
    <div className={CARD}>
      <SectionHeader icon={DollarSign} label={t('amazonListing.productSummary.keyFacts')} color="neutral" />
      <div className="space-y-2">
        <div className="flex items-center gap-2"><span className={LABEL}>{t('amazonListing.productSummary.priceRange')}</span><span className={TAG_BLUE}>{data.price_range}</span></div>
        {points.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {points.map((dp, i) => <span key={i} className={TAG_NEUTRAL}>{dp}</span>)}
          </div>
        )}
      </div>
    </div>
  )
}

export function PotentialCard({ data }: { data: ProductSummary['potential'] }) {
  const { t } = useTranslation()
  const growth = asArray(data.growth_opportunities)
  const segments = asArray(data.untapped_segments)
  return (
    <div className="rounded-xl border-2 border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50 to-primary-50 dark:from-primary-900/20 dark:to-primary-900/20 p-5">
      <SectionHeader icon={Rocket} label={t('amazonListing.productSummary.potential')} color="purple" />
      <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-3">{data.optimization_potential}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1 mb-1.5"><TrendingUp className="h-3 w-3 text-green-500" /><span className={LABEL}>{t('amazonListing.productSummary.growthOpportunities')}</span></div>
          <div className="space-y-1">{growth.map((g, i) => <div key={i} className={TAG_GREEN}>{g}</div>)}</div>
        </div>
        <div>
          <div className="flex items-center gap-1 mb-1.5"><Target className="h-3 w-3 text-primary-500" /><span className={LABEL}>{t('amazonListing.productSummary.untappedSegments')}</span></div>
          <div className="space-y-1">{segments.map((s, i) => <div key={i} className={TAG_PURPLE}>{s}</div>)}</div>
        </div>
      </div>
    </div>
  )
}
